'use strict';

var _ = require('lodash');
var assign = require('object-assign');
var EventEmitter = require('events').EventEmitter;

var AppDispatcher = require('../core/AppDispatcher.jsx');
var AccountTypeActionsTypes = require('../constants/AccountTypeConstants').ActionTypes;

var Validators = require('../utils/Validators.js');
var Mapper = require('../utils/Mapper');
var Products = require('../utils/Products');
var ApplicationHelper = require('../utils/ApplicationHelper');
var YourDetailsHelpers = require('../utils/YourDetailsHelpers');
var YourDetailsRetrieve = require('../utils/YourDetailsRetrieve');
var YourDetailsValidations = require('../utils/YourDetailsValidations.js').default;
var ClientEditabilityRules = require('../utils/ClientEditabilityRules.js').default;

var ApplicationConstants = require('../constants/ApplicationConstants');
var SelectCustomerActionsTypes = require('../constants/SelectCustomerConstants.js').ActionTypes;
var FormKeys = ApplicationConstants.FormKeys;
var ApplicationActionTypes = ApplicationConstants.ActionTypes;
var AddressActionTypes = require('../constants/AddressConstants.js').ActionTypes;
var Constants = require('../constants/YourDetailsConstants');
var SelectCustomerConstants = require('../constants/SelectCustomerConstants');
var MappingConfigurations = Constants.MappingConfigurations;
var AccountTypeCodes = ApplicationConstants.AccountTypeCodes;
var ActionTypes = Constants.ActionTypes;

var CHANGE_EVENT = 'change';
var _applicationData, _applicationLinkType;
var _businessYourDetailsData;
var _productId, _clientOin = 0, _client;
var _accountType;
var _formKey;
var _validationRule;
var _customAttributeName;
var _tfnExemptionList = [];

function prePopulateClientWithExisting(entity) {
    if (!entity) {
        return entity;
    }

    if (_client && _client.customerType === ApplicationConstants.ClientTypes.BUSINESS) {
        // prepopulate client
        entity = _.merge(entity, _client);

        if (_productId === ApplicationConstants.ProductIds.INVESTMENT &&
            (_accountType === AccountTypeCodes.SMSF || _accountType === AccountTypeCodes.TRUST)) {
            entity.oin = undefined;
        } else {
            // apply editablity rules
            var rules = ClientEditabilityRules.getRules(_accountType, _productId, _client);
            entity = _.merge(entity, rules);
        }
    }
    // existing client is an individual, non person entities (trust/company/smsf) should not have an oin
    else if (_client && _client.customerType === ApplicationConstants.ClientTypes.INDIVIDUAL) {
        entity.oin = undefined;
    }

    return entity;
}


function businessYourDetailsStoreInit() {
    _formKey = Products.getFormKey(_accountType, _productId);
    _businessYourDetailsData = {
        oin: _clientOin,
        businessNameLabel: undefined,
        businessActivityLabel: undefined,
        businessName: undefined,
        primaryBusinessActivity: undefined,
        isPrimaryBusinessInvesting: undefined,
        isCharity: undefined,
        countryEstablished: (_formKey !== FormKeys.SMSF_INVESTMENT) ? Constants.Values.COUNTRY_AUSTRALIA : undefined,
        hasTfnExemption: (_formKey === FormKeys.SMSF_INVESTMENT || _formKey === FormKeys.PARTNERSHIP_INVESTMENT) ? undefined : '',
        tfnNumber: undefined,
        tfnExemptionReason: undefined,
        tfnWithConsent: undefined,
        tfnConsentFlag: undefined,
        abnLabel: undefined,
        abn: undefined,
        addressInfo: _.cloneDeep(Constants.Properties.AddressInfo),
        mobileNumber: undefined,
        homeNumber: undefined,
        workNumber: undefined,
        emailAddress: undefined,
        businessNameReadonly: undefined,
        primaryBusinessActivityReadonly: undefined,
        countryEstablishedReadonly: undefined,
        tfnNumberReadonly: undefined,
        mobileNumberReadonly: undefined,
        homeNumberReadonly: undefined,
        workNumberReadonly: undefined,
        emailAddressReadonly: undefined,
        isOtherCountryTaxResidentReadonly: undefined,
        otherCountryTaxResidenceReadonly: undefined,
        abnReadonly: undefined,
        isAccountDesignation: ApplicationConstants.YesNoValues.NO,
        accountDesignationName: undefined,
        errorList: [],
        errorListForSummary: []
    };

    // pre-populate formData with existing client data if any
    _businessYourDetailsData = prePopulateClientWithExisting(_businessYourDetailsData);

    _setAbnNameLabel(_formKey);
    _setBusinessLabel(_accountType);

    _validationRule = YourDetailsValidations.getRulesForBusiness(_formKey);
    _customAttributeName = YourDetailsValidations.getCustomAttributeNames(_formKey);

    //Bypassing field validations for phonenumber and emailaddress if its readonly 
    if(_businessYourDetailsData.workNumberReadonly) {
        delete _validationRule.workNumber;
    }

    if(_businessYourDetailsData.mobileNumberReadonly) {
        delete _validationRule.mobileNumber;
    }

    if(_businessYourDetailsData.emailAddressReadonly) {
        delete _validationRule.emailAddress;
    }
     
}

function setClient(client) {
    _client = client;
    if (_applicationData &&
        _applicationData.originationSource === ApplicationConstants.OriginationSources.IRESS &&
        _applicationLinkType === SelectCustomerConstants.ApplicationLinkTypes.EXISTING_LISTED_CLIENT) {
        // existing client in FMS takes precedence over XPLAN but cifid, kycFlag and kycType will be retained on selecting client
        if (ApplicationHelper.isCfpAdvised(_applicationData)) {
            _client = _.assign(client,
            {
                cif: _.get(_businessYourDetailsData, 'cif', client.cif),
                kycFlag: _.get(_businessYourDetailsData, 'kycFlag', client.kycFlag),
                kycType: _.get(_businessYourDetailsData, 'kycType', client.kycType)
            });
        }
        _businessYourDetailsData = prePopulateClientWithExisting(_businessYourDetailsData);
    }
}

function setClientOin(clientOin) {
    _clientOin = clientOin;
    _businessYourDetailsData.oin = clientOin;
}

function getValidationRules(fieldId) {
    var rules = _.clone(_validationRule);
    rules = YourDetailsValidations.validationRulesForBusinessInit(rules, _businessYourDetailsData);
    return YourDetailsValidations.getValidationRules(fieldId, rules);
}

function _setBusinessLabel(accountType) {
    var businessNameLabel, businessActivityLabel;
    switch (accountType) {
        case ApplicationConstants.AccountTypeCodes.COMPANY:
            businessNameLabel =  'Full name of company';
            businessActivityLabel = 'Primary business activity';
            break;
        case ApplicationConstants.AccountTypeCodes.SMSF:
            businessNameLabel =  'Full name of super fund';
            businessActivityLabel = 'Primary trust activity';
            break;
        case ApplicationConstants.AccountTypeCodes.TRUST:
            businessNameLabel =  'Full name of trust';
            businessActivityLabel = 'Primary trust activity';
            break;
        case ApplicationConstants.AccountTypeCodes.PARTNERSHIP:
            businessNameLabel =  'Full name of partnership';
            businessActivityLabel = 'Primary business activity';
            break;
        default:
            businessNameLabel = 'Full name of business';
            businessActivityLabel = 'Primary business activity';
            break;
    }

    _businessYourDetailsData.businessNameLabel = businessNameLabel;
    _businessYourDetailsData.businessActivityLabel = businessActivityLabel;
}

function _setAbnNameLabel(formKey) {
    switch (formKey){
        case FormKeys.COMPANY_INVESTMENT:
            _businessYourDetailsData.abnLabel = 'ABN/ARBN/ARSN';
            break;
        case FormKeys.COMPANY_COMMINSURE:
        case FormKeys.COMPANY_CHALLENGER:
            _businessYourDetailsData.abnLabel = 'ABN/ARBN';
            break;
        case FormKeys.PARTNERSHIP_INVESTMENT:
            _businessYourDetailsData.abnLabel = 'ABN/ARBN/ARSN';
            break;
        default:
            _businessYourDetailsData.abnLabel = 'ABN/ARBN/ARSN';
            break;
    }
}

function setProductId(productId) {
    _productId = productId;
}

function setAccountType(accountType) {
    _accountType = accountType;
    businessYourDetailsStoreInit();
}

function updateYourDetailsData(fieldId, fieldValue) {
    switch (fieldId) {
        case 'isAccountDesignation':
            if (fieldValue === ApplicationConstants.YesNoValues.NO) {
                _businessYourDetailsData.accountDesignationName = '';
            }
            break;
        case 'tfnConsentFlag':
            if (fieldValue === ApplicationConstants.YesNoValues.YES) {
                _businessYourDetailsData.tfnWithConsent = '';
            }
            break;
        case 'hasTfnExemption':
            if (_businessYourDetailsData['hasTfnExemption'] !== fieldValue) {
                _businessYourDetailsData.tfnExemptionReason = '';
                _businessYourDetailsData.tfnConsentFlag = '';
                if (!_businessYourDetailsData.tfnNumberReadonly) {
                    _businessYourDetailsData.tfnNumber = '';
                }
                _businessYourDetailsData.tfnWithConsent = '';
            }
            break;
        default:
            break;
    }

    if (fieldId in _businessYourDetailsData) {
        _businessYourDetailsData[fieldId] = fieldValue;
        if (fieldId === 'hasTfnExemption' && fieldValue === ApplicationConstants.Clear) {
            _businessYourDetailsData.hasTfnExemption = ''
        }
    }

}

function validateField(fieldId, fieldValue, addressInfo, addressCustomAttr) {
    var rules = (!addressInfo) ? getValidationRules(fieldId) : YourDetailsValidations.getAddressValidationRules(addressInfo, fieldId, null, true);

    var errors = _businessYourDetailsData.errorList;
    if (!rules) {
        Validators.clearErrorListForField(errors, fieldId);
        return;
    }
    else{
        var data = {};
        data[fieldId] = fieldValue;
        Validators.validateField(data, rules, addressCustomAttr || _customAttributeName, errors, fieldId);
    }
}

function validateForSave() {
    if (!_accountType || !ApplicationHelper.isBusinessAccountType(_accountType)) {
        return;
    }

    var rules = YourDetailsValidations.getRulesForSave(_formKey);
    var customAttributeName = YourDetailsValidations.getCustomAttributeNames(_formKey, true);

    _businessYourDetailsData.errorList = Validators
        .validateAll(_businessYourDetailsData, rules, customAttributeName);
    _businessYourDetailsData.errorListForSummary = Validators
        .validateAll(_businessYourDetailsData, rules, customAttributeName, undefined, 'Your details');
}

function validateAll() {
    var errors = validate(_businessYourDetailsData);
    _businessYourDetailsData.errorList = errors.inlineErrorList;
    _businessYourDetailsData.errorListForSummary = errors.summaryErrorList;
}

function validate(record, index){
    var rules = getValidationRules(index);

    var inlineErrorList = Validators.validateAll(record, rules, _customAttributeName);
    var summaryErrorList = Validators.validateAll(record, rules, _customAttributeName, undefined, 'Your details');

    inlineErrorList = inlineErrorList.concat(validateAddress());
    summaryErrorList = insertAddressValidationSummary(record, rules, summaryErrorList, validateAddress(true));
    
    return {inlineErrorList: inlineErrorList, summaryErrorList: summaryErrorList};
}

//So that summary errors are in order, we need to insert address errors.
//Depending on the form, address is inserted after specific field.
function insertAddressValidationSummary(record, rule, summaryErrorList, addressSummary){
    var field;
    switch(_formKey){
        case FormKeys.COMPANY_INVESTMENT:
        case FormKeys.TRUST_INVESTMENT:
        case FormKeys.TRUST_COMMINSURE:
        case FormKeys.TRUST_CHALLENGER:
        case FormKeys.SMSF_COMMINSURE:
        case FormKeys.SMSF_CHALLENGER:
            if (record.hasTfnExemption === ApplicationConstants.YesNoValues.YES){
                field = 'tfnExemptionReason';
            }else if (record.hasTfnExemption === ApplicationConstants.YesNoValues.NO){
                field = 'tfnNumber';
            }else {
                field = 'countryEstablished';
            }
            break;
        case FormKeys.SMSF_INVESTMENT:
        case FormKeys.PARTNERSHIP_INVESTMENT:
            field = 'tfnNumber';
            break;
        case FormKeys.COMPANY_COMMINSURE:
        case FormKeys.COMPANY_CHALLENGER:
            field = 'abn';
            break;
        default:
        break;
    }
    
    var secondHalf = summaryErrorList.splice(YourDetailsHelpers.getIndexForInsert(rule, field, summaryErrorList));
    var errorList = summaryErrorList.concat(addressSummary, secondHalf);
    return errorList;
}

function validateAddressField(fieldId, fieldValue){
    validateField(fieldId, fieldValue, _businessYourDetailsData.addressInfo, YourDetailsValidations.getAddressCustomAttributeNamesForBusiness(_businessYourDetailsData.addressInfo));
    removeAustraliaStateValidations(fieldId);
}

function removeAustraliaStateValidations(fieldName){
    var errors = _businessYourDetailsData.errorList;
    if (fieldName === 'resCountry'){
        _.remove(errors, (o) => { return o.id.indexOf('cityStatePostCode') !== -1 });
        _.remove(errors, (o) => { return o.id.indexOf('resAusState') !== -1 });
        _.remove(errors, (o) => { return o.id.indexOf('postCode') !== -1 });
    }
    else if(fieldName === 'postalCountry'){
        _.remove(errors, (o) => { return o.id.indexOf('postalCityStatePostCode') !== -1 });
        _.remove(errors, (o) => { return o.id.indexOf('postalAusState') !== -1 });
        _.remove(errors, (o) => { return o.id.indexOf('postalPostCode') !== -1 });
    }
}

function validateAddress(forSummary) {
    var addressInfo = _businessYourDetailsData.addressInfo;
    var addressRules = YourDetailsValidations.getAddressValidationRules(addressInfo, null, false, true);

    if (forSummary){
        YourDetailsHelpers.addAddressNotFoundValidation(addressInfo, addressRules);
        return Validators.validateAll(addressInfo, addressRules, YourDetailsValidations.getAddressCustomAttributeNamesForBusiness(addressInfo), '', 'Your details');
    }
    else{
        return Validators.validateAll(addressInfo, addressRules, YourDetailsValidations.getAddressCustomAttributeNamesForBusiness(addressInfo));
    }
}

function saveAddressIsFound(id, result, model, value){
    saveBusinessAddressInfo(model, result);
    var address = 'postal';
    Validators.clearErrorListForField(_businessYourDetailsData.errorList, id);
    //Only residential address has PO Box validation.
    if (model === 'isResAddressFound'){
        address = 'residential';
        if (result){
            validateAddressField(address, value);
        }
    }
    saveBusinessAddressInfo(address, value);
}

function saveBusinessAddressInfo(fieldId, fieldValue) {
    _businessYourDetailsData.addressInfo[fieldId] = fieldValue;
    YourDetailsHelpers.postAddressInfoSave(_businessYourDetailsData.addressInfo, fieldId);
}

function setYourDetailsDataFromExistingApp (data) {
    _businessYourDetailsData.businessName = data.businessName;
    _businessYourDetailsData.businessNameReadonly = data.businessNameReadonly;
    _businessYourDetailsData.primaryBusinessActivity = data.primaryBusinessActivity;
    _businessYourDetailsData.primaryBusinessActivityReadonly = data.primaryBusinessActivityReadonly
    _businessYourDetailsData.isPrimaryBusinessInvesting = data.isPrimaryBusinessInvesting;
    _businessYourDetailsData.isCharity = data.isCharity;
    _businessYourDetailsData.countryEstablished = data.countryEstablished ? data.countryEstablished.country : '';
    _businessYourDetailsData.countryEstablishedReadonly = data.countryEstablished ? data.countryEstablished.countryReadonly : false;
    _businessYourDetailsData.hasTfnExemption = data.tfnInfo.hasTfnExemption;
    _businessYourDetailsData.tfnNumber = data.tfnInfo.tfnNumber;
    _businessYourDetailsData.tfnNumberReadonly = data.tfnInfo.tfnNumberReadonly;
    _businessYourDetailsData.tfnExemptionReason = data.tfnInfo.exemptionReason;
    _businessYourDetailsData.exemptionReasonReadonly = data.tfnInfo.exemptionReasonReadonly;
    _businessYourDetailsData.tfnConsentFlag = data.tfnInfo.tfnConsentFlag;
    _businessYourDetailsData.tfnWithConsent = data.tfnInfo.tfnWithConsent;
    _businessYourDetailsData.abn = data.abn;
    _businessYourDetailsData.abnReadonly = data.abnReadonly;
    YourDetailsRetrieve.componentsRetrieveService.loadResidentialAddressInfo(data.residentialAddress, _businessYourDetailsData.addressInfo);
    YourDetailsRetrieve.componentsRetrieveService.loadPostalAddressInfo(data, _businessYourDetailsData.addressInfo);
    _businessYourDetailsData.mobileNumber = data.contactDetails.mobile;
    _businessYourDetailsData.homeNumber = data.contactDetails.home;
    _businessYourDetailsData.workNumber = data.contactDetails.work;
    _businessYourDetailsData.emailAddress = data.contactDetails.email; 
    _businessYourDetailsData.mobileNumberReadonly = data.contactDetails.mobileReadonly;
    _businessYourDetailsData.homeNumberReadonly = data.contactDetails.homeReadonly;
    _businessYourDetailsData.workNumberReadonly = data.contactDetails.workReadonly;
    _businessYourDetailsData.emailAddressReadonly = data.contactDetails.emailReadonly;
    _businessYourDetailsData.accountDesignationName = data.accountDesignation.accountDesignationName;
    _businessYourDetailsData.isAccountDesignation = data.accountDesignation.isAccountDesignation;
}

function isMainSection(action){
    return action.index === undefined || action.index === '';
}

var BusinessYourDetailsStore = assign({}, EventEmitter.prototype, {
    emitChange: function () {
        this.emit(CHANGE_EVENT);
    },

    addChangeListener: function (callback) {
        this.on(CHANGE_EVENT, callback);
    },

    removeChangeListener: function (callback) {
        this.removeListener(CHANGE_EVENT, callback);
    },

    getYourDetailsData: function () {
        return _.cloneDeep(_businessYourDetailsData);  //send a clone so that consumer doesn't mess up the store model 
    },

    getErrorListForSummary: function () {
        return _businessYourDetailsData.errorListForSummary;
    },

    getTfnExemptionList: function () {
        return _tfnExemptionList;
    },

    getYourDetailsModelForSave: function () {
        var busAddress = _.cloneDeep(_businessYourDetailsData.addressInfo);
        if (!busAddress.isResAddressFound) {
            busAddress.residential = '';
        }
        if (!busAddress.isPostalAddressFound) {
            busAddress.postal = '';
        }
        var data = {
            businessName: _businessYourDetailsData.businessName,
            businessNameReadonly: _businessYourDetailsData.businessNameReadonly,
            primaryBusinessActivity: _businessYourDetailsData.primaryBusinessActivity,
            primaryBusinessActivityReadonly: _businessYourDetailsData.primaryBusinessActivityReadonly,
            isPrimaryBusinessInvesting: _businessYourDetailsData.isPrimaryBusinessInvesting,
            isCharity: _businessYourDetailsData.isCharity,
            countryEstablished: { country: _businessYourDetailsData.countryEstablished, countryReadonly: _businessYourDetailsData.countryEstablishedReadonly},
            tfnInfo: {
                tfnNumber: _businessYourDetailsData.tfnNumber,
                tfnNumberReadonly: _businessYourDetailsData.tfnNumberReadonly,
                exemptionReason: _businessYourDetailsData.tfnExemptionReason,
                exemptionReasonReadonly: _businessYourDetailsData.exemptionReasonReadonly,
                hasTfnExemption: _businessYourDetailsData.hasTfnExemption,
                tfnConsentFlag: _businessYourDetailsData.tfnConsentFlag,
                tfnWithConsent: _businessYourDetailsData.tfnWithConsent
            },
            abn: _businessYourDetailsData.abn,
            abnReadonly: _businessYourDetailsData.abnReadonly,
            residentialAddress: Mapper.objectMapperConfigKeysOnly(busAddress, MappingConfigurations.ResidentialAddressSave),
            postalAddress: Mapper.objectMapperConfigKeysOnly(busAddress, MappingConfigurations.PostalAddressSave),
            contactDetails: {
                mobile: _businessYourDetailsData.mobileNumber,
                home: _businessYourDetailsData.homeNumber,
                work: _businessYourDetailsData.workNumber,
                email: _businessYourDetailsData.emailAddress,
                mobileReadonly: _businessYourDetailsData.mobileNumberReadonly,
                homeReadonly: _businessYourDetailsData.homeNumberReadonly,
                workReadonly: _businessYourDetailsData.workNumberReadonly,
                emailReadonly: _businessYourDetailsData.emailAddressReadonly
            },
            oin: _businessYourDetailsData.oin,
            accountDesignation: {
                isAccountDesignation: _businessYourDetailsData.isAccountDesignation,
                accountDesignationName: _businessYourDetailsData.accountDesignationName
            }
        };

        return data;
    }
});

BusinessYourDetailsStore.dispatchToken = AppDispatcher.register(function (action) {

    switch (action.actionType) {
        case ApplicationActionTypes.NEW_APPLICATION_INITIALISED:
            setProductId(action.productId);
            break;
        case AccountTypeActionsTypes.SELECT_ACCOUNT_TYPE:
            setAccountType(action.accountType, action.isRetrieve);
            break;
        case ApplicationActionTypes.APPLICATION_RETRIEVE_SUCCESS:
            _applicationData = action.applicationData;
            setProductId(action.applicationData.productId);
            setAccountType(action.applicationData.form.accountType);
            if (ApplicationHelper.isBusinessAccountType(action.applicationData.form.accountType)) {
                setYourDetailsDataFromExistingApp(action.applicationData.form.yourDetails);
            }
            break;
        case ActionTypes.UPDATE_YOUR_DETAILS:
            if (isMainSection(action) && ApplicationHelper.isBusinessAccountType(_accountType)){
                updateYourDetailsData(action.fieldId, action.fieldValue);
                validateField(action.fieldId, action.fieldValue);
            }
            break;
        case ActionTypes.SAVE_BUSINESS_ADDRESS:
            if (isMainSection(action)){
                saveBusinessAddressInfo(action.fieldId, action.fieldValue);
                validateAddressField(action.fieldId, action.fieldValue);
            }
            break;
        case ApplicationActionTypes.VALIDATE_APPLICATION:
            if (ApplicationHelper.isBusinessAccountType(action.accountType)) {
                validateAll();
            }
            break;
        case ApplicationActionTypes.APPLICATION_SAVE_VALIDATE:
            validateForSave();
            break;
        case ApplicationActionTypes.LOAD_TFN_EXEMPTION_LIST:
            _tfnExemptionList = action.tfnExemptionList;
            break;
        case ActionTypes.YOUR_DETAILS_REMOVE_VALIDATION:
            YourDetailsHelpers.removeValidation(_businessYourDetailsData.errorList, action.listofIds, Validators);
            break;
        case AddressActionTypes.RECEIVED_VALIDATION_RESULT:
            if (isMainSection(action) && ApplicationHelper.isBusinessAccountType(_accountType)){
                saveAddressIsFound(action.id, action.result, action.model, action.value);
            }
            break;
        case SelectCustomerActionsTypes.SET_CLIENT_OIN:
            setClientOin(action.clientOin);
            break;
        case SelectCustomerActionsTypes.CLIENT_RETRIEVE_COMPLETED:
            setClient(action.client);
            break;
        case ApplicationActionTypes.SET_APPLICATION_LINK_TYPE:
            _applicationLinkType = action.applicationLinkType;
            break;
        default:
            return true;
    }

    BusinessYourDetailsStore.emitChange();
    return true;

});

businessYourDetailsStoreInit();

module.exports = BusinessYourDetailsStore;




// WEBPACK FOOTER //
// C:/GH/1106/advice.noa.origination/~/eslint-loader!./stores/BusinessYourDetailsStore.js